import _ from 'lodash';

import { FolderValidationErrorType, UploadValidationErrorType } from '@models';

import i18n from '@configs/i18n';
import config from '@configs/config.json';

export interface IUploadValidationErrorDetails {
  issue: string;
  help: string;
}

let details: { [code: string]: IUploadValidationErrorDetails } | undefined =
  undefined;

// build the list of error details on demand
const getDetails = (): { [code: string]: IUploadValidationErrorDetails } => {
  if (details) {
    return details;
  }

  details = {
    [FolderValidationErrorType.MissingExperimentList]: {
      issue: i18n.t('upload_validation_issue_missingExperimentList'),
      help: i18n.t('upload_validation_help_fileNotFound', {
        file: `<name>${config.upload.experimentFileName}`,
      }),
    },
    [FolderValidationErrorType.MissingRunParameter]: {
      issue: i18n.t('upload_validation_issue_runInfoFileNotFound'),
      help: i18n.t('upload_validation_help_fileNotFound', {
        file: config.upload.runInfoFileName,
      }),
    },
    [FolderValidationErrorType.MultipleExperimentList]: {
      issue: i18n.t('upload_validation_issue_multipleExperimentLists'),
      help: i18n.t('upload_validation_help_multipleExperimentLists', {
        file: `<name>${config.upload.experimentFileName}`,
      }),
    },
  };

  // For errors with no substitutions we can just generate the map
  _.forEach(
    [
      UploadValidationErrorType.MissingFile,
      UploadValidationErrorType.InvalidFileName,
      UploadValidationErrorType.MissingContent,
      UploadValidationErrorType.InvalidFileFormat,
      UploadValidationErrorType.MissingRequiredHeaders,
      UploadValidationErrorType.InvalidTagPlate,
      UploadValidationErrorType.MissingRequiredPlateInformation,
      UploadValidationErrorType.MismatchPlateInformationAcrossSamples,
      UploadValidationErrorType.InvalidSampleInformation,
      UploadValidationErrorType.InvalidSampleDateFormat,
      UploadValidationErrorType.InvalidSampleName,
      UploadValidationErrorType.InvalidSampleNameConsecutiveUnderscore,
      UploadValidationErrorType.MismatchSampleInformationAcrossWells,
      UploadValidationErrorType.InvalidTestType,
      UploadValidationErrorType.InvalidExternalSubjectId,
      UploadValidationErrorType.MissingRequiredSampleInformation,
      UploadValidationErrorType.SampleMissingWells,
      UploadValidationErrorType.InvalidWell,
      UploadValidationErrorType.WellsRepeatedAcrossSamples,
      UploadValidationErrorType.InvalidSourceType,
      UploadValidationErrorType.InvalidMaterial,
      UploadValidationErrorType.InvalidPrimerLot,
      UploadValidationErrorType.ExtraHeaders,
      UploadValidationErrorType.ExtraColumns,
    ],
    (errorType) => {
      if (!details) {
        return;
      }
      details[errorType] = {
        issue: i18n.t(
          `upload_validation_issue_${_.lowerFirst(
            UploadValidationErrorType[errorType]
          )}`
        ),
        help: i18n.t(
          `upload_validation_help_${_.lowerFirst(
            UploadValidationErrorType[errorType]
          )}`
        ),
      };
    }
  );

  return details;
};

export const issue = (code: string): string => {
  const error = getDetails()[code];
  return error?.issue || `Unknown error for code "${code}"`;
};

export const help = (code: string): string => {
  const error = getDetails()[code];
  return error?.help || '';
};
